import { useEffect, useState } from "react";
import PageMeta from "../../tailadmin/components/common/PageMeta";
import EcommerceMetrics from "../../tailadmin/components/ecommerce/EcommerceMetrics";
import DashboardBarChart from "../../components/charts/DashboardBarChart";
import PieChart from "../../tailadmin/components/ecommerce/PieChart";
import { useAuth } from "../../context/AuthContext";
import reports from "../../services/reports";
import { ChartData, StatusData } from "../../types";
import Skeleton from "../../components/ui/Skeleton";
import { FiLink, FiInfo } from "react-icons/fi";

const Tooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-2">
    <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
    <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden w-48 -translate-x-1/2 rounded bg-gray-800 p-2 text-center text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-50">
      {text}
      <div className="absolute left-1/2 top-full -ml-1 h-0 w-0 border-4 border-solid border-transparent border-t-gray-800"></div>
    </div>
  </div>
);

export default function Home() {
  const { getUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<number | null>(30);
  const [studentGrowth, setStudentGrowth] = useState<ChartData[]>([]);
  const [workoutGrowth, setWorkoutGrowth] = useState<ChartData[]>([]);
  const [studentStatus, setStudentStatus] = useState<StatusData[]>([]);
  const [appointmentStats, setAppointmentStats] = useState<StatusData[]>([]);
  const [avaliationStats, setAvaliationStats] = useState<{ count: number }>({
    count: 0,
  });
  const [expiredWorkouts, setExpiredWorkouts] = useState<{
    expired_count: number;
  }>({ expired_count: 0 });
  const [linkStats, setLinkStats] = useState<
    { label: string; clicks: string }[]
  >([]);

  useEffect(() => {
    async function fetchData() {
      const user = getUser();
      if (user?.id) {
        setLoading(true);
        try {
          const filter = dateFilter || undefined;

          // Parallelize independent requests
          const [
            students,
            workouts,
            studentsStatusData,
            appointmentsData,
            avaliationsData,
            expiredData,
            linkData,
          ] = await Promise.all([
            reports.crescimentoUsers(user.id, filter),
            reports.workoutsGrow(user.id, filter),
            reports.getStudentStatus(user.id),
            reports.getAppointmentStats(user.id, filter),
            reports.getAvaliationStats(user.id, filter),
            reports.getExpiredWorkouts(user.id),
            reports.getLinkStats(user.id),
          ]);

          setStudentGrowth(students);
          setWorkoutGrowth(workouts);
          setStudentStatus(studentsStatusData);
          setAppointmentStats(appointmentsData);
          setAvaliationStats(avaliationsData);
          setExpiredWorkouts(expiredData);
          setLinkStats(linkData);
        } catch (error) {
          console.error("Error fetching dashboard data", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
    fetchData();
  }, [getUser, dateFilter]);

  const totalStudents = studentGrowth.reduce(
    (acc, curr) => acc + curr.count,
    0,
  );
  const totalWorkouts = workoutGrowth.reduce(
    (acc, curr) => acc + curr.count,
    0,
  );
  const totalAppointments = appointmentStats.reduce(
    (acc, curr) => acc + curr.count,
    0,
  );
  const totalAvaliations = avaliationStats.count;
  const totalExpiredWorkouts = expiredWorkouts.expired_count;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    return dateStr.split("-").reverse().slice(0, 2).join("/");
  };

  const studentCategories = studentGrowth.map((d) => formatDate(d.date));
  const studentData = studentGrowth.map((d) => d.count);

  const workoutCategories = workoutGrowth.map((d) => formatDate(d.date));
  const workoutData = workoutGrowth.map((d) => d.count);

  const studentStatusLabels = studentStatus.map((d) => d.status);
  const studentStatusData = studentStatus.map((d) => d.count);

  const appointmentLabels = appointmentStats.map((d) => d.status);
  const appointmentData = appointmentStats.map((d) => d.count);

  // Link Metrics
  const totalLinkClicks = linkStats.reduce(
    (acc, curr) => acc + (parseInt(curr.clicks) || 0),
    0,
  );
  const linkLabels = linkStats.map((l) => l.label || "Sem Rótulo");
  const linkData = linkStats.map((l) => parseInt(l.clicks) || 0);

  const filterOptions = [
    { label: "7 dias", value: 7 },
    { label: "15 dias", value: 15 },
    { label: "30 dias", value: 30 },
    { label: "45 dias", value: 45 },
    { label: "90 dias", value: 90 },
  ];

  return (
    <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 2xl:px-10">
      {/* Header - Mobile First */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-2xl font-bold text-transparent dark:from-purple-400 dark:via-pink-400 dark:to-blue-400 sm:text-3xl md:text-4xl">
            Dashboard
          </h2>
          <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-400 sm:mt-2 sm:text-sm">
            Insights completos sobre o crescimento de seus alunos, treinos e
            agendamentos
          </p>
        </div>
        <div className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:overflow-visible sm:px-0">
          <div className="flex gap-2 sm:flex-wrap">
            {filterOptions.map((option) => (
              <button
                key={option.label}
                onClick={() => setDateFilter(option.value)}
                className={`flex-shrink-0 min-h-[44px] rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-300 touch-manipulation sm:min-h-0 sm:rounded-lg ${
                  dateFilter === option.value
                    ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50 dark:from-purple-500 dark:to-pink-500"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <PageMeta title="Dashboard | Easyfit" description="Dashboard Easyfit" />

      {loading ? (
        <div className="grid grid-cols-12 gap-3 sm:gap-4 md:gap-6">
          <div className="col-span-12 space-y-4 xl:col-span-12 sm:space-y-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-6 xl:grid-cols-4 2xl:gap-7.5">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white p-3 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] sm:p-4 md:rounded-2xl md:p-6"
                >
                  <Skeleton className="h-9 w-9 rounded-full sm:h-10 sm:w-10" />
                  <div className="mt-3 flex items-end justify-between sm:mt-4">
                    <div>
                      <Skeleton className="mb-2 h-5 w-20 sm:h-6 sm:w-24" />
                      <Skeleton className="h-3 w-28 sm:h-4 sm:w-32" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {[...Array(4)].map((_, i) => (
            <div key={i} className="col-span-12 xl:col-span-6">
              <div className="rounded-xl border border-stroke bg-white px-3 pb-3 pt-4 shadow-default dark:border-strokedark dark:bg-boxdark sm:rounded-sm sm:px-5 sm:pb-5 sm:pt-7.5 md:px-7.5">
                <div className="mb-2 justify-between gap-4 sm:mb-3 sm:flex">
                  <div>
                    <Skeleton className="h-5 w-32 sm:h-6 sm:w-40" />
                  </div>
                </div>
                <div className="mb-2">
                  <Skeleton className="h-[240px] w-full sm:h-[300px] md:h-[350px]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-3 sm:gap-4 md:gap-6">
          <div className="col-span-12 space-y-4 xl:col-span-12 sm:space-y-6">
            <EcommerceMetrics
              totalStudents={totalStudents}
              totalWorkouts={totalWorkouts}
              totalAppointments={totalAppointments}
              totalAvaliations={totalAvaliations}
              totalExpiredWorkouts={totalExpiredWorkouts}
            />
          </div>

          <div className="col-span-12 xl:col-span-6">
            <DashboardBarChart
              title="Novos Alunos"
              data={studentData}
              categories={studentCategories}
              color="#a855f7"
              barName="Alunos"
            />
          </div>

          <div className="col-span-12 xl:col-span-6">
            <DashboardBarChart
              title="Treinos Criados"
              data={workoutData}
              categories={workoutCategories}
              color="#3b82f6"
              barName="Treinos"
            />
          </div>

          <div className="col-span-12 xl:col-span-6">
            <PieChart
              title="Status dos Alunos"
              data={studentStatusData}
              labels={studentStatusLabels}
            />
          </div>

          <div className="col-span-12 xl:col-span-6">
            <PieChart
              title="Agendamentos"
              data={appointmentData}
              labels={appointmentLabels}
            />
          </div>

          {/* Meu Link Section - Mobile First */}
          <div className="col-span-12 mt-6 sm:mt-8">
            <div className="mb-4 flex items-center gap-3 sm:mb-6 sm:gap-4">
              <h2 className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-xl font-bold text-transparent dark:from-orange-400 dark:to-pink-400 sm:text-2xl">
                <FiLink className="text-orange-600 dark:text-orange-400" />
                Meu Link
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-orange-200 via-pink-200 to-transparent dark:from-orange-800 dark:via-pink-800"></div>
            </div>

            <div className="grid grid-cols-12 gap-3 sm:gap-4 md:gap-6">
              {/* Total Clicks Card */}
              <div className="col-span-12 xl:col-span-4">
                <div className="group h-full rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-3 shadow-sm transition-all duration-300 dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01] sm:p-4 md:rounded-2xl md:p-6 md:hover:shadow-xl">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110 sm:h-14 sm:w-14"
                    style={{
                      background:
                        "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
                      boxShadow: "0 10px 25px -5px rgba(249, 115, 22, 0.5)",
                    }}
                  >
                    <FiLink className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                  </div>

                  <div className="mt-3 flex items-end justify-between sm:mt-5">
                    <div>
                      <div className="flex items-center">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                          Cliques Totais
                        </span>
                        <Tooltip text="Total de acessos via link" />
                      </div>
                      <h4
                        className="mt-1.5 bg-clip-text text-2xl font-bold text-transparent sm:mt-2 sm:text-3xl"
                        style={{
                          backgroundImage:
                            "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
                        }}
                      >
                        {totalLinkClicks}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clicks Chart */}
              <div className="col-span-12 xl:col-span-8">
                <DashboardBarChart
                  title="Cliques por Botão"
                  data={linkData}
                  categories={linkLabels}
                  color="#ec4899"
                  barName="Cliques"
                  tooltipText="Quantidade de cliques em cada botão do seu link. Exemplo: 20 cliques no botão 'WhatsApp'."
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
